import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Wifi, WifiOff, ChevronDown } from 'lucide-react';
import { getFileUrl, getInitials } from '../../utils/dashboardHelpers';

interface Instance {
  id: string;
  nome: string;
  statusConexao: string;
}

interface Contato {
  id: string;
  nome: string;
  telefone: string;
  avatarUrl?: string;
}

interface NovoChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedInstance: string;
  onInstanceChange: (id: string) => void;
  availableInstances: Instance[];
  modalContatoSearch: string;
  onContatoSearchChange: (value: string) => void;
  modalContatosLoading: boolean;
  modalContatos: Contato[];
  onSelectContato: (telefone: string) => void;
  onStartNewChat: () => void;
}

const NovoChatModal: React.FC<NovoChatModalProps> = ({
  open,
  onOpenChange,
  selectedInstance,
  onInstanceChange,
  availableInstances,
  modalContatoSearch,
  onContatoSearchChange,
  modalContatosLoading,
  modalContatos,
  onSelectContato,
  onStartNewChat,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] dark:bg-slate-600 max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="dark:text-white">Iniciar Nova Conversa</DialogTitle>
          <DialogDescription className="dark:text-white">
            Digite o telefone do contato com DDI (ex: 5511999998888) ou o ID do grupo.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="instanceSelect" className="text-right dark:text-white">
              Instância *
            </Label>
            <div className="col-span-3 instance-selector-dropdown">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex h-10 w-full rounded-md justify-between items-center bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate dark:text-white">
                        {selectedInstance
                          ? availableInstances.find(inst => inst.id === selectedInstance)?.nome || 'Selecionar Instância'
                          : 'Selecionar Instância'
                        }
                      </span>
                      {selectedInstance && (availableInstances.find(inst => inst.id === selectedInstance)?.statusConexao === 'connected') ? (
                        <Wifi className="text-green-600 flex-shrink-0" size={16} />
                      ) : selectedInstance ? (
                        <WifiOff className="text-orange-600 flex-shrink-0" size={16} />
                      ) : null}
                    </div>
                    <ChevronDown size={12} className="opacity-70 ml-2 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-full rounded-md dark:bg-slate-800 max-h-[200px] overflow-y-auto">
                  <DropdownMenuLabel className="text-xs">Instâncias Disponíveis</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {availableInstances.length > 0 ? (
                    availableInstances.map((instance) => (
                      <DropdownMenuItem
                        key={instance.id}
                        className={`flex items-center gap-3 cursor-pointer p-2 ${selectedInstance === instance.id ? 'bg-slate-100 dark:bg-slate-700 font-bold' : ''}`}
                        onSelect={() => { onInstanceChange(instance.id); }}
                      >
                        <div className="flex items-center justify-center w-4 h-4 rounded-full border-2 border-gray-300">
                          {selectedInstance === instance.id && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          )}
                        </div>
                        {instance.statusConexao === 'connected' ? (
                          <Wifi className="text-green-600" size={16} />
                        ) : (
                          <WifiOff className="text-orange-600" size={16} />
                        )}
                        <div className="flex-1 overflow-hidden text-left">
                          <div className="text-sm truncate">{instance.nome}</div>
                          <div className="text-[10px] text-gray-500">
                            {instance.statusConexao === 'connected' ? 'Conectado' : 'Desconectado'}
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <div className="px-2 py-4 text-center text-xs text-gray-500">Nenhuma instância encontrada</div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="contatoSearch" className="text-right dark:text-white">
              Pesquisar
            </Label>
            <Input
              id="contatoSearch"
              value={modalContatoSearch}
              onChange={(e) => {
                const value = e.target.value;
                onContatoSearchChange(value);
              }}
              placeholder="Digite o telefone ou pesquise por nome"
              className="col-span-3 w-full dark:bg-slate-700 dark:text-white"
            />
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1 dark:text-white">Contatos</div>
            <ScrollArea className="h-64 w-full rounded border border-gray-200 dark:border-slate-700 p-2 dark:bg-slate-700">
              {modalContatosLoading ? (
                <div className="text-sm text-gray-500 dark:text-white">Carregando...</div>
              ) : modalContatos.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-white">Nenhum contato encontrado.</div>
              ) : (
                <div className="space-y-2">
                  {modalContatos.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-2 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer w-full"
                      onClick={() => onSelectContato(c.telefone)}
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <Avatar className="h-8 w-8 mr-3 flex-shrink-0">
                          <AvatarImage src={getFileUrl(c.avatarUrl)} alt={c.nome} />
                          <AvatarFallback className="bg-gray-300 dark:bg-slate-600 text-white text-sm">{getInitials(c.nome)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate dark:text-white">
                            {(c.nome || 'Sem nome').length > 30 ? `${(c.nome || 'Sem nome').substring(0, 30)}...` : (c.nome || 'Sem nome')}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{c.telefone}</div>
                        </div>
                      </div>
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onSelectContato(c.telefone); }}>Iniciar</Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">Cancelar</Button>
          </DialogClose>
          <Button type="button" onClick={() => onStartNewChat()}>Iniciar Conversa</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NovoChatModal;
